/**
 * PassageHighlightToolbar — the three highlighter pens plus Clear.
 *
 * Arming a pen switches the passage into draw mode. Tapping the armed pen again puts it down.
 *
 * Colour is never the sole indicator of which pen is armed (WCAG 1.4.1): the armed swatch also
 * takes a ring and a check glyph. Each swatch carries a border so the light tints stay visible
 * against the cream paper.
 */

import { Check, Highlighter } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  HIGHLIGHT_COLORS,
  HIGHLIGHT_COLOR_ORDER,
  type HighlightColor,
} from '@/lib/passageHighlights';

const COLOR_LABELS: Record<HighlightColor, string> = {
  yellow: 'Yellow highlighter',
  green: 'Green highlighter',
  pink: 'Pink highlighter',
};

export interface PassageHighlightToolbarProps {
  armedColor: HighlightColor | null;
  onArm: (color: HighlightColor | null) => void;
  onClear: () => void;
  /** Disables Clear when there is nothing to clear. */
  hasHighlights: boolean;
}

export function PassageHighlightToolbar({
  armedColor,
  onArm,
  onClear,
  hasHighlights,
}: PassageHighlightToolbarProps) {
  return (
    <div className="mb-2 flex items-center gap-1.5" role="group" aria-label="Passage highlighter">
      {/* Leading affordance so it reads as "highlighter", not three loose colour dots. */}
      <span
        className="flex items-center gap-1 pr-0.5 font-body text-sm font-medium text-otter-earth-700"
        aria-hidden="true"
      >
        <Highlighter className="h-4 w-4" />
        <span className="hidden sm:inline">Highlight</span>
      </span>

      {HIGHLIGHT_COLOR_ORDER.map((color) => {
        const isArmed = armedColor === color;
        return (
          <button
            key={color}
            type="button"
            aria-label={COLOR_LABELS[color]}
            aria-pressed={isArmed}
            // Toggle: tapping the armed pen puts it down and restores normal scroll/select.
            onClick={() => onArm(isArmed ? null : color)}
            className={cn(
              'flex min-h-11 min-w-11 items-center justify-center rounded-xl transition-all',
              isArmed ? 'ring-2 ring-otter-earth-700 ring-offset-1' : 'hover:bg-otter-sand'
            )}
          >
            <span
              aria-hidden="true"
              className="flex h-6 w-6 items-center justify-center rounded-full border border-otter-earth-700/30"
              style={{ backgroundColor: HIGHLIGHT_COLORS[color] }}
            >
              {isArmed && <Check className="h-3.5 w-3.5 text-otter-earth-800" strokeWidth={3} />}
            </span>
          </button>
        );
      })}

      <button
        type="button"
        onClick={onClear}
        disabled={!hasHighlights}
        className={cn(
          'ml-auto min-h-11 rounded-xl px-3 font-body text-sm text-otter-earth-700 transition-colors',
          hasHighlights ? 'hover:bg-otter-sand' : 'cursor-not-allowed opacity-40'
        )}
      >
        Clear
      </button>
    </div>
  );
}
